import type { OnlineOrderRow } from './mock/onlineOrders';
import type { PosOrderRow } from './mock/posOrders';
import type { OnlineTicketSalesRow } from './mock/onlineTicketSales';
import type { PosTicketSalesRow } from './mock/posTicketSales';
import type { OnlineSalesByCityRow } from './mock/onlineSalesByCity';
import type { OnlineSalesByPostalCodeRow } from './mock/onlineSalesByPostalCode';
import type { OnlineSalesByDayRow } from './mock/onlineSalesByDay';
import type { OnlineSalesByDayTicketRow } from './mock/onlineSalesByDayTicket';
import type { PosSalesByDayRow } from './mock/posSalesByDay';
import type { ReportTableConfig } from './reportTableTypes';

const currencyColumn = {
    format: 'currency' as const,
    align: 'right' as const,
};

const countColumn = {
    align: 'right' as const,
    summable: true,
};

// SQL returns 0–1 ratios; footer shows an average (not a sum).
const percentColumn = {
    format: 'percent' as const,
    align: 'right' as const,
    summable: true,
};

const onlineOrdersConfig: ReportTableConfig<OnlineOrderRow> = {
    categories: ['ORDERS', 'CUSTOMER', 'TICKET DETAILS'],
    defaultSelectedColumnIds: [
        'orderNumber',
        'orderDate',
        'firstName',
        'lastName',
        'company',
        'email',
        'phone',
        'address',
        'city',
        'province',
        'postalCode',
        'ticketQty',
        'subtotal',
        'discountCode',
        'discountAmount',
        'taxAmount',
        'fdFee',
        'creditCardFee',
        'totalPaid',
        'deliveredAt',
        'trackingSource',
        'payout',
    ],
    columns: [
        {
            id: 'orderNumber',
            label: 'Order #',
            category: 'ORDERS',
            getValue: (row) => row.orderNumber,
        },
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'ORDERS',
            getValue: (row) => row.orderDate,
            sortType: 'datetime',
            format: 'dateTimeLong',
            exportFormat: 'dateTimeIso',
        },
        {
            id: 'address',
            label: 'Address',
            category: 'ORDERS',
            getValue: (row) => row.address,
            sortable: false,
            muted: true,
        },
        {
            id: 'postalCode',
            label: 'Postal Code',
            category: 'ORDERS',
            getValue: (row) => row.postalCode,
            sortable: false,
            muted: true,
        },
        {
            id: 'discountCode',
            label: 'Discount Code',
            category: 'ORDERS',
            getValue: (row) => row.discountCode,
            sortable: false,
            muted: true,
        },
        {
            id: 'discountAmount',
            label: 'Discount Amount',
            category: 'ORDERS',
            getValue: (row) => row.discountAmount,
            ...currencyColumn,
            sortable: false,
            muted: true,
            summable: true,
        },
        {
            id: 'taxAmount',
            label: 'Tax Amount',
            category: 'ORDERS',
            getValue: (row) => row.taxAmount,
            ...currencyColumn,
            sortable: false,
            muted: true,
            summable: true,
        },
        {
            id: 'trackingSource',
            label: 'Tracking Source',
            category: 'ORDERS',
            getValue: (row) => row.trackingSource,
            sortable: false,
            muted: true,
        },
        {
            id: 'payout',
            label: 'Payout',
            category: 'ORDERS',
            getValue: (row) => row.payout,
            ...currencyColumn,
            sortable: false,
            muted: true,
            summable: true,
        },
        {
            id: 'firstName',
            label: 'First Name',
            category: 'CUSTOMER',
            getValue: (row) => row.firstName,
        },
        {
            id: 'lastName',
            label: 'Last Name',
            category: 'CUSTOMER',
            getValue: (row) => row.lastName,
        },
        { id: 'email', label: 'Email', category: 'CUSTOMER', getValue: (row) => row.email },
        {
            id: 'phone',
            label: 'Phone',
            category: 'CUSTOMER',
            getValue: (row) => row.phone,
            sortable: false,
        },
        { id: 'city', label: 'City', category: 'CUSTOMER', getValue: (row) => row.city },
        { id: 'province', label: 'Prov', category: 'CUSTOMER', getValue: (row) => row.province },
        {
            id: 'company',
            label: 'Company',
            category: 'CUSTOMER',
            getValue: (row) => row.company,
            sortable: false,
            muted: true,
        },
        {
            id: 'ticketQty',
            label: 'Ticket Qty',
            category: 'TICKET DETAILS',
            getValue: (row) => row.ticketQty,
            ...countColumn,
        },
        {
            id: 'subtotal',
            label: 'Subtotal',
            category: 'TICKET DETAILS',
            getValue: (row) => row.subtotal,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'fdFee',
            label: 'FD+ Fee',
            category: 'TICKET DETAILS',
            getValue: (row) => row.fdFee,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'creditCardFee',
            label: 'Credit Card Fee',
            category: 'TICKET DETAILS',
            getValue: (row) => row.creditCardFee,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'totalPaid',
            label: 'Total Paid',
            category: 'TICKET DETAILS',
            getValue: (row) => row.totalPaid,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'deliveredAt',
            label: 'Delivered At',
            category: 'TICKET DETAILS',
            getValue: (row) => row.deliveredAt,
            sortType: 'datetime',
            format: 'dateTimeLong',
            exportFormat: 'dateTimeIso',
        },
    ],
};

const posOrdersConfig: ReportTableConfig<PosOrderRow> = {
    categories: ['ORDERS', 'CUSTOMER', 'TICKET DETAILS'],
    defaultSelectedColumnIds: [
        'orderNumber',
        'orderDate',
        'day',
        'posDeviceId',
        'posName',
        'ticketQty',
        'tranType',
        'subtotal',
        'taxAmount',
        'fdFee',
        'processingFee',
        'tip',
        'totalPaid',
        'payout',
        'email',
        'postalCode',
        'note',
    ],
    columns: [
        {
            id: 'orderNumber',
            label: 'Order #',
            category: 'ORDERS',
            getValue: (row) => row.orderNumber,
        },
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'ORDERS',
            getValue: (row) => row.orderDate,
            sortType: 'datetime',
            format: 'dateTimeLong',
            exportFormat: 'dateTimeIso',
        },
        { id: 'day', label: 'Day', category: 'ORDERS', getValue: (row) => row.day },
        {
            id: 'posDeviceId',
            label: 'POS Device ID',
            category: 'ORDERS',
            getValue: (row) => row.posDeviceId,
            sortable: false,
            muted: true,
        },
        { id: 'posName', label: 'POS Name', category: 'ORDERS', getValue: (row) => row.posName },
        { id: 'tranType', label: 'Tran Type', category: 'ORDERS', getValue: (row) => row.tranType },
        {
            id: 'note',
            label: 'Note',
            category: 'ORDERS',
            getValue: (row) => row.note,
            sortable: false,
            muted: true,
        },
        {
            id: 'email',
            label: 'Email',
            category: 'CUSTOMER',
            getValue: (row) => row.email,
            sortable: false,
            muted: true,
        },
        {
            id: 'postalCode',
            label: 'Postal Code',
            category: 'CUSTOMER',
            getValue: (row) => row.postalCode,
            sortable: false,
            muted: true,
        },
        {
            id: 'ticketQty',
            label: 'Ticket Qty',
            category: 'TICKET DETAILS',
            getValue: (row) => row.ticketQty,
            ...countColumn,
        },
        {
            id: 'subtotal',
            label: 'Subtotal',
            category: 'TICKET DETAILS',
            getValue: (row) => row.subtotal,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'taxAmount',
            label: 'Tax Amount',
            category: 'TICKET DETAILS',
            getValue: (row) => row.taxAmount,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'fdFee',
            label: 'FD+ Fee',
            category: 'TICKET DETAILS',
            getValue: (row) => row.fdFee,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'processingFee',
            label: 'Processing Fee',
            category: 'TICKET DETAILS',
            getValue: (row) => row.processingFee,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'tip',
            label: 'Tip',
            category: 'TICKET DETAILS',
            getValue: (row) => row.tip,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'totalPaid',
            label: 'Total Paid',
            category: 'TICKET DETAILS',
            getValue: (row) => row.totalPaid,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'payout',
            label: 'Payout',
            category: 'TICKET DETAILS',
            getValue: (row) => row.payout,
            ...currencyColumn,
            summable: true,
        },
    ],
};

const onlineTicketSalesConfig: ReportTableConfig<OnlineTicketSalesRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'ticketName',
        'type',
        'totalQty',
        'soldQty',
        'discountedQty',
        'compedQty',
        'faceValue',
        'adjValue',
    ],
    columns: [
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        { id: 'type', label: 'Type', category: 'SUMMARY', getValue: (row) => row.type },
        {
            id: 'totalQty',
            label: 'Total Qty',
            category: 'SUMMARY',
            getValue: (row) => row.totalQty,
            ...countColumn,
        },
        {
            id: 'soldQty',
            label: 'Sold Qty',
            category: 'SUMMARY',
            getValue: (row) => row.soldQty,
            ...countColumn,
        },
        {
            id: 'discountedQty',
            label: 'Discounted Qty',
            category: 'SUMMARY',
            getValue: (row) => row.discountedQty,
            ...countColumn,
        },
        {
            id: 'compedQty',
            label: 'Comped Qty',
            category: 'SUMMARY',
            getValue: (row) => row.compedQty,
            ...countColumn,
        },
        {
            id: 'faceValue',
            label: 'Face Value',
            category: 'SUMMARY',
            getValue: (row) => row.faceValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'adjValue',
            label: 'Adj Value',
            category: 'SUMMARY',
            getValue: (row) => row.adjValue,
            ...currencyColumn,
            summable: true,
        },
    ],
};

const posTicketSalesConfig: ReportTableConfig<PosTicketSalesRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'ticketName',
        'type',
        'totalQty',
        'cardQty',
        'cashQty',
        'freeQty',
        'totalValue',
        'cardValue',
        'cashValue',
    ],
    columns: [
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        { id: 'type', label: 'Type', category: 'SUMMARY', getValue: (row) => row.type },
        {
            id: 'totalQty',
            label: 'Total Qty',
            category: 'SUMMARY',
            getValue: (row) => row.totalQty,
            ...countColumn,
        },
        {
            id: 'cardQty',
            label: 'Card Qty',
            category: 'SUMMARY',
            getValue: (row) => row.cardQty,
            ...countColumn,
        },
        {
            id: 'cashQty',
            label: 'Cash Qty',
            category: 'SUMMARY',
            getValue: (row) => row.cashQty,
            ...countColumn,
        },
        {
            id: 'freeQty',
            label: 'Free Qty',
            category: 'SUMMARY',
            getValue: (row) => row.freeQty,
            ...countColumn,
        },
        {
            id: 'totalValue',
            label: 'Total Value',
            category: 'SUMMARY',
            getValue: (row) => row.totalValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'cardValue',
            label: 'Card Value',
            category: 'SUMMARY',
            getValue: (row) => row.cardValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'cashValue',
            label: 'Cash Value',
            category: 'SUMMARY',
            getValue: (row) => row.cashValue,
            ...currencyColumn,
            summable: true,
        },
    ],
};

const onlineSalesByCityConfig: ReportTableConfig<OnlineSalesByCityRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'city',
        'province',
        'orderCount',
        'ticketCount',
        'discountCount',
        'discountAmount',
        'payout',
    ],
    columns: [
        { id: 'city', label: 'City', category: 'SUMMARY', getValue: (row) => row.city },
        { id: 'province', label: 'Prov', category: 'SUMMARY', getValue: (row) => row.province },
        {
            id: 'orderCount',
            label: 'Order Count',
            category: 'SUMMARY',
            getValue: (row) => row.orderCount,
            ...countColumn,
        },
        {
            id: 'ticketCount',
            label: 'Ticket Count',
            category: 'SUMMARY',
            getValue: (row) => row.ticketCount,
            ...countColumn,
        },
        {
            id: 'discountCount',
            label: 'Disc. Count',
            category: 'SUMMARY',
            getValue: (row) => row.discountCount,
            ...countColumn,
        },
        {
            id: 'discountAmount',
            label: 'Disc. Amt',
            category: 'SUMMARY',
            getValue: (row) => row.discountAmount,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'payout',
            label: 'Payout',
            category: 'SUMMARY',
            getValue: (row) => row.payout,
            ...currencyColumn,
            summable: true,
        },
    ],
};

const onlineSalesByPostalCodeConfig: ReportTableConfig<OnlineSalesByPostalCodeRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'postalCode',
        'city',
        'province',
        'orderCount',
        'ticketCount',
        'discountCount',
        'discountAmount',
        'payout',
        'infoLink',
    ],
    columns: [
        {
            id: 'postalCode',
            label: 'Postal Code',
            category: 'SUMMARY',
            getValue: (row) => row.postalCode,
        },
        { id: 'city', label: 'City', category: 'SUMMARY', getValue: (row) => row.city },
        { id: 'province', label: 'Prov', category: 'SUMMARY', getValue: (row) => row.province },
        {
            id: 'orderCount',
            label: 'Order Count',
            category: 'SUMMARY',
            getValue: (row) => row.orderCount,
            ...countColumn,
        },
        {
            id: 'ticketCount',
            label: 'Ticket Count',
            category: 'SUMMARY',
            getValue: (row) => row.ticketCount,
            ...countColumn,
        },
        {
            id: 'discountCount',
            label: 'Disc. Count',
            category: 'SUMMARY',
            getValue: (row) => row.discountCount,
            ...countColumn,
        },
        {
            id: 'discountAmount',
            label: 'Disc. Amt',
            category: 'SUMMARY',
            getValue: (row) => row.discountAmount,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'payout',
            label: 'Payout',
            category: 'SUMMARY',
            getValue: (row) => row.payout,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'infoLink',
            label: 'Info Link',
            category: 'SUMMARY',
            getValue: (row) => row.infoLink,
            sortable: false,
            muted: true,
        },
    ],
};

const onlineSalesByDayConfig: ReportTableConfig<OnlineSalesByDayRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'orderDate',
        'orderCount',
        'ticketCount',
        'discountCount',
        'discountAmount',
        'payout',
    ],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
        },
        {
            id: 'orderCount',
            label: 'Order Count',
            category: 'SUMMARY',
            getValue: (row) => row.orderCount,
            ...countColumn,
        },
        {
            id: 'ticketCount',
            label: 'Ticket Count',
            category: 'SUMMARY',
            getValue: (row) => row.ticketCount,
            ...countColumn,
        },
        {
            id: 'discountCount',
            label: 'Discs Count',
            category: 'SUMMARY',
            getValue: (row) => row.discountCount,
            ...countColumn,
        },
        {
            id: 'discountAmount',
            label: 'Disc. Amt',
            category: 'SUMMARY',
            getValue: (row) => row.discountAmount,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'payout',
            label: 'Payout',
            category: 'SUMMARY',
            getValue: (row) => row.payout,
            ...currencyColumn,
            summable: true,
        },
    ],
};

const onlineSalesByDayTicketConfig: ReportTableConfig<OnlineSalesByDayTicketRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: ['orderDate', 'ticketName', 'type', 'count'],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
        },
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        { id: 'type', label: 'Type', category: 'SUMMARY', getValue: (row) => row.type },
        {
            id: 'count',
            label: 'Count',
            category: 'SUMMARY',
            getValue: (row) => row.count,
            ...countColumn,
        },
    ],
};

const posSalesByDayConfig: ReportTableConfig<PosSalesByDayRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'orderDate',
        'day',
        'orderCount',
        'ticketQty',
        'cardQty',
        'cashQty',
        'freeQty',
        'totalValue',
        'cardValue',
        'cashValue',
        'orderType',
    ],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        {
            id: 'orderCount',
            label: 'Order Count',
            category: 'SUMMARY',
            getValue: (row) => row.orderCount,
            ...countColumn,
        },
        {
            id: 'ticketQty',
            label: 'Ticket Qty',
            category: 'SUMMARY',
            getValue: (row) => row.ticketQty,
            ...countColumn,
        },
        {
            id: 'cardQty',
            label: 'Card Qty',
            category: 'SUMMARY',
            getValue: (row) => row.cardQty,
            ...countColumn,
        },
        {
            id: 'cashQty',
            label: 'Cash Qty',
            category: 'SUMMARY',
            getValue: (row) => row.cashQty,
            ...countColumn,
        },
        {
            id: 'freeQty',
            label: 'Free Qty',
            category: 'SUMMARY',
            getValue: (row) => row.freeQty,
            ...countColumn,
        },
        {
            id: 'totalValue',
            label: 'Total Value',
            category: 'SUMMARY',
            getValue: (row) => row.totalValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'cardValue',
            label: 'Card Value',
            category: 'SUMMARY',
            getValue: (row) => row.cardValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'cashValue',
            label: 'Cash Value',
            category: 'SUMMARY',
            getValue: (row) => row.cashValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'orderType',
            label: 'Order Type',
            category: 'SUMMARY',
            getValue: (row) => row.orderType,
            sortable: false,
            muted: true,
        },
    ],
};

type PosSalesByDayHourRow = PosSalesByDayRow & { hour: number };

const posSalesByDayHourConfig: ReportTableConfig<PosSalesByDayHourRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'orderDate',
        'day',
        'hour',
        'orderCount',
        'ticketQty',
        'cardQty',
        'cashQty',
        'freeQty',
        'totalValue',
        'cardValue',
        'cashValue',
        'orderType',
    ],
    columns: [
        ...posSalesByDayConfig.columns.slice(0, 2),
        {
            id: 'hour',
            label: 'Hour',
            category: 'SUMMARY',
            getValue: (row) => row.hour,
            summable: false,
        },
        ...posSalesByDayConfig.columns.slice(2, -1),
        posSalesByDayConfig.columns[posSalesByDayConfig.columns.length - 1],
    ],
};

type PosTicketsSoldByDayRow = {
    orderDate: string;
    day: string;
    ticketName: string;
    ticketType: string;
    totalQty: number;
    cardQty: number;
    cashQty: number;
    freeQty: number;
    totalValue: number;
    cardValue: number;
    cashValue: number;
};

const posTicketQtyValueColumns = [
    { id: 'totalQty', label: 'Total Qty', getValue: (row: PosTicketsSoldByDayRow) => row.totalQty },
    { id: 'cardQty', label: 'Card Qty', getValue: (row: PosTicketsSoldByDayRow) => row.cardQty },
    { id: 'cashQty', label: 'Cash Qty', getValue: (row: PosTicketsSoldByDayRow) => row.cashQty },
    { id: 'freeQty', label: 'Free Qty', getValue: (row: PosTicketsSoldByDayRow) => row.freeQty },
    {
        id: 'totalValue',
        label: 'Total Value',
        getValue: (row: PosTicketsSoldByDayRow) => row.totalValue,
        ...currencyColumn,
        summable: true,
    },
    {
        id: 'cardValue',
        label: 'Card Value',
        getValue: (row: PosTicketsSoldByDayRow) => row.cardValue,
        ...currencyColumn,
        summable: true,
    },
    {
        id: 'cashValue',
        label: 'Cash Value',
        getValue: (row: PosTicketsSoldByDayRow) => row.cashValue,
        ...currencyColumn,
        summable: true,
    },
] as const;

const posTicketsSoldByDayConfig: ReportTableConfig<PosTicketsSoldByDayRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'orderDate',
        'day',
        'ticketName',
        'ticketType',
        'totalQty',
        'cardQty',
        'cashQty',
        'freeQty',
        'totalValue',
        'cardValue',
        'cashValue',
    ],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        {
            id: 'ticketType',
            label: 'Ticket Type',
            category: 'SUMMARY',
            getValue: (row) => row.ticketType,
        },
        ...posTicketQtyValueColumns.map((col) => ({
            ...col,
            category: 'SUMMARY' as const,
            ...(col.id.includes('Qty') ? countColumn : {}),
        })),
    ],
};

type PosSalesByDayDeviceRow = Omit<PosSalesByDayRow, 'orderType'> & {
    pos: string;
    orderType: string;
};

const posSalesByDayDeviceConfig: ReportTableConfig<PosSalesByDayDeviceRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'orderDate',
        'day',
        'pos',
        'orderCount',
        'ticketQty',
        'cardQty',
        'cashQty',
        'freeQty',
        'totalValue',
        'cardValue',
        'cashValue',
        'orderType',
    ],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        { id: 'pos', label: 'POS', category: 'SUMMARY', getValue: (row) => row.pos },
        {
            id: 'orderCount',
            label: 'Order Count',
            category: 'SUMMARY',
            getValue: (row) => row.orderCount,
            ...countColumn,
        },
        {
            id: 'ticketQty',
            label: 'Ticket Qty',
            category: 'SUMMARY',
            getValue: (row) => row.ticketQty,
            ...countColumn,
        },
        {
            id: 'cardQty',
            label: 'Card Qty',
            category: 'SUMMARY',
            getValue: (row) => row.cardQty,
            ...countColumn,
        },
        {
            id: 'cashQty',
            label: 'Cash Qty',
            category: 'SUMMARY',
            getValue: (row) => row.cashQty,
            ...countColumn,
        },
        {
            id: 'freeQty',
            label: 'Free Qty',
            category: 'SUMMARY',
            getValue: (row) => row.freeQty,
            ...countColumn,
        },
        {
            id: 'totalValue',
            label: 'Total Value',
            category: 'SUMMARY',
            getValue: (row) => row.totalValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'cardValue',
            label: 'Card Value',
            category: 'SUMMARY',
            getValue: (row) => row.cardValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'cashValue',
            label: 'Cash Value',
            category: 'SUMMARY',
            getValue: (row) => row.cashValue,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'orderType',
            label: 'Order Type',
            category: 'SUMMARY',
            getValue: (row) => row.orderType,
            sortable: false,
            muted: true,
        },
    ],
};

type PosTicketsSoldByDayDeviceRow = PosTicketsSoldByDayRow & { pos: string };

const posTicketsSoldByDayDeviceConfig: ReportTableConfig<PosTicketsSoldByDayDeviceRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'orderDate',
        'day',
        'pos',
        'ticketName',
        'ticketType',
        'totalQty',
        'cardQty',
        'cashQty',
        'freeQty',
        'totalValue',
        'cardValue',
        'cashValue',
    ],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        { id: 'pos', label: 'POS', category: 'SUMMARY', getValue: (row) => row.pos },
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        {
            id: 'ticketType',
            label: 'Ticket Type',
            category: 'SUMMARY',
            getValue: (row) => row.ticketType,
        },
        ...posTicketQtyValueColumns.map((col) => ({
            ...col,
            category: 'SUMMARY' as const,
            ...(col.id.includes('Qty') ? countColumn : {}),
        })),
    ],
};

type PosTipsByDayRow = { orderDate: string; day: string; totalTips: number; orderType: string };

const posTipsByDayConfig: ReportTableConfig<PosTipsByDayRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: ['orderDate', 'day', 'totalTips', 'orderType'],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        {
            id: 'totalTips',
            label: 'Total Tips',
            category: 'SUMMARY',
            getValue: (row) => row.totalTips,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'orderType',
            label: 'Order Type',
            category: 'SUMMARY',
            getValue: (row) => row.orderType,
            sortable: false,
            muted: true,
        },
    ],
};

type PosTipsByDayDeviceRow = PosTipsByDayRow & { pos: string };

const posTipsByDayDeviceConfig: ReportTableConfig<PosTipsByDayDeviceRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: ['orderDate', 'day', 'pos', 'totalTips', 'orderType'],
    columns: [
        {
            id: 'orderDate',
            label: 'Order Date',
            category: 'SUMMARY',
            getValue: (row) => row.orderDate,
            sortType: 'date',
            format: 'dateShort',
            exportFormat: 'dateShort',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        { id: 'pos', label: 'POS', category: 'SUMMARY', getValue: (row) => row.pos },
        {
            id: 'totalTips',
            label: 'Total Tips',
            category: 'SUMMARY',
            getValue: (row) => row.totalTips,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'orderType',
            label: 'Order Type',
            category: 'SUMMARY',
            getValue: (row) => row.orderType,
            sortable: false,
            muted: true,
        },
    ],
};

type ScanByHourRow = { date: string; day: string; hour: number; checkInCount: number };

const scanByHourConfig: ReportTableConfig<ScanByHourRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: ['date', 'day', 'hour', 'checkInCount'],
    columns: [
        {
            id: 'date',
            label: 'Date',
            category: 'SUMMARY',
            getValue: (row) => row.date,
            sortType: 'date',
            format: 'dateLong',
            exportFormat: 'dateIso',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        {
            id: 'hour',
            label: 'Hour',
            category: 'SUMMARY',
            getValue: (row) => row.hour,
        },
        {
            id: 'checkInCount',
            label: 'Check-in Count',
            category: 'SUMMARY',
            getValue: (row) => row.checkInCount,
            ...countColumn,
        },
    ],
};

type ScanByHourTicketTypeRow = ScanByHourRow & { ticketName: string; ticketType: string };

const scanByHourTicketTypeConfig: ReportTableConfig<ScanByHourTicketTypeRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: ['date', 'day', 'hour', 'ticketName', 'ticketType', 'checkInCount'],
    columns: [
        ...scanByHourConfig.columns.slice(0, 3),
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        {
            id: 'ticketType',
            label: 'Ticket Type',
            category: 'SUMMARY',
            getValue: (row) => row.ticketType,
        },
        scanByHourConfig.columns[3],
    ],
};

type ScanByHourScannerRow = ScanByHourRow & { scanner: string };

const scanByHourScannerConfig: ReportTableConfig<ScanByHourScannerRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: ['date', 'day', 'hour', 'scanner', 'checkInCount'],
    columns: [
        ...scanByHourConfig.columns.slice(0, 3),
        { id: 'scanner', label: 'Scanner', category: 'SUMMARY', getValue: (row) => row.scanner },
        scanByHourConfig.columns[3],
    ],
};

type ScanByHourScannerTicketTypeRow = ScanByHourScannerRow & {
    ticketName: string;
    ticketType: string;
};

const scanByHourScannerTicketTypeConfig: ReportTableConfig<ScanByHourScannerTicketTypeRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'date',
        'day',
        'hour',
        'scanner',
        'ticketName',
        'ticketType',
        'checkInCount',
    ],
    columns: [
        ...scanByHourScannerConfig.columns.slice(0, 4),
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        {
            id: 'ticketType',
            label: 'Ticket Type',
            category: 'SUMMARY',
            getValue: (row) => row.ticketType,
        },
        scanByHourScannerConfig.columns[4],
    ],
};

type ScanByHalfHourScannerTicketTypeRow = {
    date: string;
    day: string;
    periodStart: string;
    periodEnd: string;
    scanner: string;
    ticketName: string;
    ticketType: string;
    checkInCount: number;
};

const scanByHalfHourScannerTicketTypeConfig: ReportTableConfig<ScanByHalfHourScannerTicketTypeRow> =
    {
        categories: ['SUMMARY'],
        defaultSelectedColumnIds: [
            'date',
            'day',
            'periodStart',
            'periodEnd',
            'scanner',
            'ticketName',
            'ticketType',
            'checkInCount',
        ],
        columns: [
            {
                id: 'date',
                label: 'Date',
                category: 'SUMMARY',
                getValue: (row) => row.date,
                sortType: 'date',
                format: 'dateShort',
            },
            { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
            {
                id: 'periodStart',
                label: 'Period Start',
                category: 'SUMMARY',
                getValue: (row) => row.periodStart,
            },
            {
                id: 'periodEnd',
                label: 'Period End',
                category: 'SUMMARY',
                getValue: (row) => row.periodEnd,
            },
            {
                id: 'scanner',
                label: 'Scanner',
                category: 'SUMMARY',
                getValue: (row) => row.scanner,
            },
            {
                id: 'ticketName',
                label: 'Ticket Name',
                category: 'SUMMARY',
                getValue: (row) => row.ticketName,
            },
            {
                id: 'ticketType',
                label: 'Ticket Type',
                category: 'SUMMARY',
                getValue: (row) => row.ticketType,
            },
            {
                id: 'checkInCount',
                label: 'Check-in Count',
                category: 'SUMMARY',
                getValue: (row) => row.checkInCount,
                ...countColumn,
            },
        ],
    };

type ScanByDayScannerTicketTypeRow = {
    date: string;
    day: string;
    scanner: string;
    ticketName: string;
    ticketType: string;
    checkInCount: number;
};

const scanByDayScannerTicketTypeConfig: ReportTableConfig<ScanByDayScannerTicketTypeRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'date',
        'day',
        'scanner',
        'ticketName',
        'ticketType',
        'checkInCount',
    ],
    columns: [
        {
            id: 'date',
            label: 'Date',
            category: 'SUMMARY',
            getValue: (row) => row.date,
            sortType: 'date',
            format: 'dateShort',
        },
        { id: 'day', label: 'Day', category: 'SUMMARY', getValue: (row) => row.day },
        { id: 'scanner', label: 'Scanner', category: 'SUMMARY', getValue: (row) => row.scanner },
        {
            id: 'ticketName',
            label: 'Ticket Name',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        {
            id: 'ticketType',
            label: 'Ticket Type',
            category: 'SUMMARY',
            getValue: (row) => row.ticketType,
        },
        {
            id: 'checkInCount',
            label: 'Check-in Count',
            category: 'SUMMARY',
            getValue: (row) => row.checkInCount,
            ...countColumn,
        },
    ],
};

type ScanByDayTicketTypeRow = Omit<ScanByDayScannerTicketTypeRow, 'scanner'>;

const scanByDayTicketTypeConfig: ReportTableConfig<ScanByDayTicketTypeRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: ['date', 'day', 'ticketName', 'ticketType', 'checkInCount'],
    columns: scanByDayScannerTicketTypeConfig.columns.filter((col) => col.id !== 'scanner'),
};

type ScannerPerformanceRow = {
    scanner: string;
    name: string;
    id: string;
    brand: string;
    model: string;
    network: string;
    carrier: string;
    cellular: string;
    totalScans: number;
    onlineScans: number;
    onlineScanPct: number;
    offlineScans: number;
    offlineScanPct: number;
    goodScans: number;
    goodScanPct: number;
    doubleScans: number;
    doubleScanPct: number;
    wrongTimeScans: number;
    wrongTimeScanPct: number;
    invalidScans: number;
    invalidScansPct: number;
    lastScan: string;
};

const scannerPerformanceConfig: ReportTableConfig<ScannerPerformanceRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'scanner',
        'name',
        'id',
        'brand',
        'model',
        'network',
        'carrier',
        'cellular',
        'totalScans',
        'onlineScans',
        'onlineScanPct',
        'offlineScans',
        'offlineScanPct',
        'goodScans',
        'goodScanPct',
        'doubleScans',
        'doubleScanPct',
        'wrongTimeScans',
        'wrongTimeScanPct',
        'invalidScans',
        'invalidScansPct',
        'lastScan',
    ],
    columns: [
        { id: 'scanner', label: 'Scanner', category: 'SUMMARY', getValue: (row) => row.scanner },
        { id: 'name', label: 'Name', category: 'SUMMARY', getValue: (row) => row.name },
        {
            id: 'id',
            label: 'Id',
            category: 'SUMMARY',
            getValue: (row) => row.id,
            sortable: false,
            muted: true,
        },
        {
            id: 'brand',
            label: 'Brand',
            category: 'SUMMARY',
            getValue: (row) => row.brand,
            sortable: false,
            muted: true,
        },
        {
            id: 'model',
            label: 'Model',
            category: 'SUMMARY',
            getValue: (row) => row.model,
            sortable: false,
            muted: true,
        },
        {
            id: 'network',
            label: 'Network',
            category: 'SUMMARY',
            getValue: (row) => row.network,
            sortable: false,
            muted: true,
        },
        {
            id: 'carrier',
            label: 'Carrier',
            category: 'SUMMARY',
            getValue: (row) => row.carrier,
            sortable: false,
            muted: true,
        },
        {
            id: 'cellular',
            label: 'Cellular',
            category: 'SUMMARY',
            getValue: (row) => row.cellular,
            sortable: false,
            muted: true,
        },
        {
            id: 'totalScans',
            label: 'Total Scans',
            category: 'SUMMARY',
            getValue: (row) => row.totalScans,
            ...countColumn,
        },
        {
            id: 'onlineScans',
            label: 'Online Scans',
            category: 'SUMMARY',
            getValue: (row) => row.onlineScans,
            ...countColumn,
        },
        {
            id: 'onlineScanPct',
            label: 'Online Scan %',
            category: 'SUMMARY',
            getValue: (row) => row.onlineScanPct,
            ...percentColumn,
        },
        {
            id: 'offlineScans',
            label: 'Offline Scans',
            category: 'SUMMARY',
            getValue: (row) => row.offlineScans,
            ...countColumn,
        },
        {
            id: 'offlineScanPct',
            label: 'Offline Scan %',
            category: 'SUMMARY',
            getValue: (row) => row.offlineScanPct,
            ...percentColumn,
        },
        {
            id: 'goodScans',
            label: 'Good Scans',
            category: 'SUMMARY',
            getValue: (row) => row.goodScans,
            ...countColumn,
        },
        {
            id: 'goodScanPct',
            label: 'Good Scan %',
            category: 'SUMMARY',
            getValue: (row) => row.goodScanPct,
            ...percentColumn,
        },
        {
            id: 'doubleScans',
            label: 'Double Scans',
            category: 'SUMMARY',
            getValue: (row) => row.doubleScans,
            ...countColumn,
        },
        {
            id: 'doubleScanPct',
            label: 'Double Scan %',
            category: 'SUMMARY',
            getValue: (row) => row.doubleScanPct,
            ...percentColumn,
        },
        {
            id: 'wrongTimeScans',
            label: 'Wrong Time Scans',
            category: 'SUMMARY',
            getValue: (row) => row.wrongTimeScans,
            ...countColumn,
        },
        {
            id: 'wrongTimeScanPct',
            label: 'Wrong Time Scan %',
            category: 'SUMMARY',
            getValue: (row) => row.wrongTimeScanPct,
            ...percentColumn,
        },
        {
            id: 'invalidScans',
            label: 'Invalid Scans',
            category: 'SUMMARY',
            getValue: (row) => row.invalidScans,
            ...countColumn,
        },
        {
            id: 'invalidScansPct',
            label: 'Invalid Scans %',
            category: 'SUMMARY',
            getValue: (row) => row.invalidScansPct,
            ...percentColumn,
        },
        {
            id: 'lastScan',
            label: 'Last Scan',
            category: 'SUMMARY',
            getValue: (row) => row.lastScan,
            sortType: 'datetime',
            format: 'dateTimeLong',
            exportFormat: 'dateTimeIso',
        },
    ],
};

type TicketScanLogRow = {
    when: string;
    result: string;
    name: string;
    scanner: string;
    deviceId: string;
    ticketName: string;
    orderNumber: string;
    customer: string;
};

const ticketScanLogConfig: ReportTableConfig<TicketScanLogRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'when',
        'result',
        'name',
        'scanner',
        'deviceId',
        'ticketName',
        'orderNumber',
        'customer',
    ],
    columns: [
        {
            id: 'when',
            label: 'When',
            category: 'SUMMARY',
            getValue: (row) => row.when,
            sortType: 'datetime',
            format: 'dateTimeLong',
            exportFormat: 'dateTimeIso',
        },
        { id: 'result', label: 'Result', category: 'SUMMARY', getValue: (row) => row.result },
        { id: 'name', label: 'Name', category: 'SUMMARY', getValue: (row) => row.name },
        { id: 'scanner', label: 'Scanner', category: 'SUMMARY', getValue: (row) => row.scanner },
        {
            id: 'deviceId',
            label: 'Device ID',
            category: 'SUMMARY',
            getValue: (row) => row.deviceId,
            muted: true,
        },
        {
            id: 'ticketName',
            label: 'Ticket',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        {
            id: 'orderNumber',
            label: 'Order #',
            category: 'SUMMARY',
            getValue: (row) => row.orderNumber,
        },
        { id: 'customer', label: 'Customer', category: 'SUMMARY', getValue: (row) => row.customer },
    ],
};

type TrackingLinkPerformanceRow = {
    source: string;
    clicks: number;
    orders: number;
    tickets: number;
    subTotal: number | null;
    discountAmount: number | null;
    taxAmount: number | null;
    fdFee: number | null;
    ccFee: number | null;
    revenue: number;
};

const trackingLinkPerformanceConfig: ReportTableConfig<TrackingLinkPerformanceRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'source',
        'clicks',
        'orders',
        'tickets',
        'subTotal',
        'discountAmount',
        'taxAmount',
        'fdFee',
        'ccFee',
        'revenue',
    ],
    columns: [
        { id: 'source', label: 'Source', category: 'SUMMARY', getValue: (row) => row.source },
        {
            id: 'clicks',
            label: 'Clicks',
            category: 'SUMMARY',
            getValue: (row) => row.clicks,
            ...countColumn,
        },
        {
            id: 'orders',
            label: 'Orders',
            category: 'SUMMARY',
            getValue: (row) => row.orders,
            ...countColumn,
        },
        {
            id: 'tickets',
            label: 'Tickets',
            category: 'SUMMARY',
            getValue: (row) => row.tickets,
            ...countColumn,
        },
        {
            id: 'subTotal',
            label: 'Sub Total',
            category: 'SUMMARY',
            getValue: (row) => row.subTotal,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'discountAmount',
            label: 'Disc Amt',
            category: 'SUMMARY',
            getValue: (row) => row.discountAmount,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'taxAmount',
            label: 'Tax Amt',
            category: 'SUMMARY',
            getValue: (row) => row.taxAmount,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'fdFee',
            label: 'FD Fee',
            category: 'SUMMARY',
            getValue: (row) => row.fdFee,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'ccFee',
            label: 'CC Fee',
            category: 'SUMMARY',
            getValue: (row) => row.ccFee,
            ...currencyColumn,
            summable: true,
        },
        {
            id: 'revenue',
            label: 'Revenue',
            category: 'SUMMARY',
            getValue: (row) => row.revenue,
            ...currencyColumn,
            summable: true,
        },
    ],
};

type TicketCountSummaryRow = {
    ticketName: string;
    type: string;
    cardQty: number;
    cashQty: number;
    freeQty: number;
    posQty: number;
    scannedQty: number;
    combinedTotal: number;
};

const ticketCountSummaryConfig: ReportTableConfig<TicketCountSummaryRow> = {
    categories: ['SUMMARY'],
    defaultSelectedColumnIds: [
        'ticketName',
        'type',
        'cardQty',
        'cashQty',
        'freeQty',
        'posQty',
        'scannedQty',
        'combinedTotal',
    ],
    columns: [
        {
            id: 'ticketName',
            label: 'Ticket',
            category: 'SUMMARY',
            getValue: (row) => row.ticketName,
        },
        { id: 'type', label: 'Type', category: 'SUMMARY', getValue: (row) => row.type },
        {
            id: 'cardQty',
            label: 'Card',
            category: 'SUMMARY',
            getValue: (row) => row.cardQty,
            ...countColumn,
        },
        {
            id: 'cashQty',
            label: 'Cash',
            category: 'SUMMARY',
            getValue: (row) => row.cashQty,
            ...countColumn,
        },
        {
            id: 'freeQty',
            label: 'Free',
            category: 'SUMMARY',
            getValue: (row) => row.freeQty,
            ...countColumn,
        },
        {
            id: 'posQty',
            label: 'POS TICKETS',
            category: 'SUMMARY',
            getValue: (row) => row.posQty,
            ...countColumn,
        },
        {
            id: 'scannedQty',
            label: 'Scanned Tickets',
            category: 'SUMMARY',
            getValue: (row) => row.scannedQty,
            ...countColumn,
        },
        {
            id: 'combinedTotal',
            label: 'COMBINED TOTAL',
            category: 'SUMMARY',
            getValue: (row) => row.combinedTotal,
            ...countColumn,
        },
    ],
};

const reportTableConfigs = {
    'online-orders': onlineOrdersConfig,
    'pos-orders': posOrdersConfig,
    'online-ticket-sales': onlineTicketSalesConfig,
    'pos-ticket-sales': posTicketSalesConfig,
    'online-sales-by-city': onlineSalesByCityConfig,
    'online-sales-by-postal-code': onlineSalesByPostalCodeConfig,
    'online-sales-by-day': onlineSalesByDayConfig,
    'online-sales-by-day-ticket': onlineSalesByDayTicketConfig,
    'pos-sales-by-day': posSalesByDayConfig,
    'pos-sales-by-day-hour': posSalesByDayHourConfig,
    'pos-tickets-sold-by-day': posTicketsSoldByDayConfig,
    'pos-sales-by-day-device': posSalesByDayDeviceConfig,
    'pos-tickets-sold-by-day-device': posTicketsSoldByDayDeviceConfig,
    'pos-tips-by-day': posTipsByDayConfig,
    'pos-tips-by-day-device': posTipsByDayDeviceConfig,
    'scan-by-hour': scanByHourConfig,
    'scan-by-hour-ticket-type': scanByHourTicketTypeConfig,
    'scan-by-hour-scanner': scanByHourScannerConfig,
    'scan-by-hour-scanner-ticket-type': scanByHourScannerTicketTypeConfig,
    'scan-by-half-hour-scanner-ticket-type': scanByHalfHourScannerTicketTypeConfig,
    'scan-by-day-scanner-ticket-type': scanByDayScannerTicketTypeConfig,
    'scan-by-day-ticket-type': scanByDayTicketTypeConfig,
    'scan-scanner-performance': scannerPerformanceConfig,
    'ticket-scan-log': ticketScanLogConfig,
    'marketing-tracking-link-performance': trackingLinkPerformanceConfig,
    'ticket-count-summary': ticketCountSummaryConfig,
} as const;

export type SupportedReportId = keyof typeof reportTableConfigs;

export function isSupportedReportId(reportId: string): reportId is SupportedReportId {
    return reportId in reportTableConfigs;
}

export function getReportTableConfig(reportId: string): ReportTableConfig | undefined {
    if (!isSupportedReportId(reportId)) {
        return undefined;
    }

    return reportTableConfigs[reportId];
}
